import { createStyles, Divider, Paper, Theme, withStyles, WithStyles } from '@material-ui/core'
import moment from 'moment'
import React from 'react'

import { ETHNICITY_MAPPER, GENDER_MAPPER, Participant } from '../../../services/Model/Participant'
import { capitalize, mapToYesOrNoWithNullable, phoneNumberFormatter, yesNoNormalizer } from '../../../services/utils'
import InfoItem from '../InfoItem/InfoItem'
import TypographyHeader from '../TypographyHeader/TypographyHeader'

interface ParticipantInfoProps extends WithStyles<typeof styles> {
	participant?: Participant
}

const PERSONAL_INFO_ITEMS = [
	{
		prop: 'firstName',
		label: 'First Name',
	},
	{
		prop: 'middleInitial',
		label: 'Middle Initial',
	},
	{
		prop: 'lastName',
		label: 'Last Name',
	},
	{
		prop: 'dateOfBirth',
		label: 'Date of Birth',
	},
]
const ADDRESS_ITEMS = [
	{
		prop: 'address1',
		label: 'Address Line 1',
	},
	{
		prop: 'address2',
		label: 'Address Line 2',
	},
	{
		prop: 'city',
		label: 'City',
	},
	{
		prop: 'state',
		label: 'State',
	},
	{
		prop: 'zip',
		label: 'Zip',
	},
]
const SECONDARY_ADDRESS_ITEMS = [
	{
		prop: 'mailingAddress1',
		label: 'Mailing Address Line 1',
	},
	{
		prop: 'mailingAddress2',
		label: 'Mailing Address Line 2',
	},
	{
		prop: 'mailingCity',
		label: 'Mailing City',
	},
	{
		prop: 'mailingState',
		label: 'Mailing State',
	},
	{
		prop: 'mailingZip',
		label: 'Mailing Zip',
	},
]
const ENROLLMENT_DATA = [
	{
		prop: 'citizen',
		label: 'Citizen',
	},
	{
		prop: 'veteran',
		label: 'Veteran',
	},
	{
		prop: 'ell',
		label: 'English Language Learner',
	},
	{
		prop: 'education',
		label: 'Education',
	},
	{
		prop: 'highestGrade',
		label: 'Highest level of education',
	},
	{
		prop: 'attendingSchool',
		label: 'Currently in School',
	},
	{
		prop: 'currentSchool',
		label: 'Current School',
	},
	{
		prop: 'working',
		label: 'Currently working',
	},
	{
		prop: 'employer',
		label: 'Employer name',
	},
	{
		prop: 'beingLaidOff',
		label: 'Laid off, or notified of layoff',
	},
	{
		prop: 'layoffDate',
		label: 'Laid off date',
	},
	{
		prop: 'layoffEmployer',
		label: 'Layoff employer name',
	},
	{
		prop: 'higherWages',
		label: 'Have you ever earned higher wages than you earn now?',
	},
	{
		prop: 'betterSkills',
		label: 'Are you looking to make better use of your skills and/or education?',
	},
	{
		prop: 'familyDependent',
		label:
			'Have you experienced a loss of income from a family member that is still impacting your financial situation?',
	},
  // {
	// 	prop: 'quest',
	// 	label: 'Quest',
	// },
]
const EDUCATION_MAPPER = {
	NO_HIGHSCHOOL: 'Did not complete High School',
	HIGH_SCHOOL: 'High School Diploma',
	GED: 'GED',
	SOME_COLLEGE: 'Some College',
	VOCATIONAL: 'Vocational School Certificate',
	ASSOCIATE: 'Associate Degree',
	BACHELORS: 'Bachelor Degree',
	MASTERS: 'Master Degree',
	PHD: 'PhD',
	NA: 'NA',
}
const RACE_MAPPER = {
	ASIAN: 'Asian',
	ISLANDER: 'Hawaiian or Pacific Islander',
	INDIGENOUS: 'Native American or Alaska Native',
	WHITE: 'White',
	BLACK: 'Black',
}

export const ParticipantInfo: React.FC<ParticipantInfoProps> = (props: ParticipantInfoProps) => {
	const { classes, participant } = props

	const enrollmentDataParser = (prop: any) => {
		if (!participant) return

		let passValue

		switch (prop) {
			case 'currentSchool':
				passValue = capitalize(participant[prop]) || ''
				break
			case 'layoffEmployer':
				passValue = capitalize(participant[prop]) || ''
				break
			case 'employer':
				passValue = capitalize(participant[prop]) || ''
				break
			case 'education':
				passValue = EDUCATION_MAPPER[participant[prop]] || ''
				break
			case 'layoffDate':
				passValue = participant[prop] ? moment(participant[prop]).format('MM-DD-YYYY') : ''
				break
			case 'employmentType':
				passValue = participant[prop] ? capitalize(participant[prop]) : ''
				break
      case 'quest':
        passValue = participant[prop] === 1 ? 'Yes' :  participant[prop] === 0 ? 'No' : ''
        break
			default:
				passValue = participant[prop] || ''
		}

		return passValue
	}

	return (
		<div className={classes.root}>
			{participant && (
				<Paper elevation={2} className={classes.participantWrapper}>
					<TypographyHeader
						label={`${participant.lastName}, ${participant.firstName} ${participant.middleInitial || ''}`}
						type="title"
					/>
					<div className={classes.infoContainer}>
						<InfoItem label={'DMS Activity Participant ID'} value={participant.activityParticipantId || ''} />
						<InfoItem label={'DMS Participant ID'} value={participant.participantId || ''} />
						{PERSONAL_INFO_ITEMS.map((pInfoItem: any, index: number) => (
							<InfoItem
								label={pInfoItem.label}
								value={
									pInfoItem.prop === 'dateOfBirth'
										? moment(participant[pInfoItem.prop]).format('MM-DD-YYYY')
										: participant[pInfoItem.prop]
								}
								key={index}
							/>
						))}

						<InfoItem label={'Email'} value={participant.email || ''} />

						<InfoItem
							label={'Phone Number'}
							value={participant.phoneNumber ? phoneNumberFormatter(participant.phoneNumber) : ''}
						/>

						<InfoItem label={'Gender'} value={GENDER_MAPPER[participant.gender] || ''} />

						<InfoItem label={'Ethnicity'} value={ETHNICITY_MAPPER[participant.ethnicity] || ''} />

						<InfoItem label={'Race'} value={participant.races.map((p: string) => RACE_MAPPER[p]).join(' / ')} />

						<InfoItem label={'Homeless'} value={yesNoNormalizer(participant.homeless)} />

						<div className={classes.addressContainer}>
							{ADDRESS_ITEMS.map((addressItem: any, index: number) => (
								<InfoItem key={index} label={addressItem.label} value={participant[addressItem.prop]} />
							))}
						</div>
						<div className={classes.addressContainer}>
							{SECONDARY_ADDRESS_ITEMS.map((addressItem: any, index: number) => (
								<InfoItem key={index} label={addressItem.label} value={participant[addressItem.prop]} />
							))}
						</div>
					</div>

					<Divider variant="middle" className={classes.divider} />

					<TypographyHeader label="Enrollment Data" type="title" />

					<div className={classes.infoContainer}>
						{ENROLLMENT_DATA.map((enrollmentItem: any, index: number) => {
							const passValue = enrollmentDataParser(enrollmentItem.prop)

							return <InfoItem key={index} label={enrollmentItem.label} value={passValue} dualCol={true} />
						})}
            {/* <div>
                <p>I confirm that I want and am available for work (including being currently employed), and one or more of the following statements is true for me: </p> 
                <ul>
                  <li>I am unemployed</li>
                  <li>I have looked for work sometime in the past 12 months</li>
                  <li>I am employed part-time but want and am available for full-time work</li>
                  <li>I am employed but am seeking a position with greater opportunities for economic advancement</li>
                </ul>
            </div>
            <div>
              {mapToYesOrNoWithNullable(participant.covid)}
            </div> */}

            {/* <div>
              <p>I confirm that one or more of the following statements is true for me:</p> 
                <ul>
                  <li>At some point since the start of the pandemic (Jan. 27, 2020 – today), I lost my source of income or earned less money (For example: employer shut down/closed during pandemic, laid off from employer during COVID, hours cut at work, loss of second job, loss of overtime hours, loss of access to clients or contracts, loss of childcare, could not report work due to COVID health concerns, could not report to work to stay home and care for a family member, etc.)</li>
                  <li>At some point since the start of the pandemic (Jan. 27, 2020 – today), I had difficulty being hired (For example: fewer job opportunities, lack of access to transit, inability to interview in an in-person environment, increased barriers to establishing my own business, impacted employment prospects for my skill set, etc.)</li>
                </ul>
            </div>
            <div>
              {mapToYesOrNoWithNullable(participant.quest)}
            </div> */}
            
						{participant.backToWork && participant.participantType !== 3 && (
							<InfoItem label='Have you experienced a change in income, 
								employment, or job opportunities due to the COVID-19 pandemic?'
								value={mapToYesOrNoWithNullable(participant.covid)} dualCol={true} />
						)}
					</div>
				</Paper>
			)}
		</div>
	)
}

const styles = (theme: Theme) =>
	createStyles({
		root: {},
		participantWrapper: {
			padding: `${theme.spacing.unit}px`,
		},
		header: {
			padding: `${theme.spacing.unit * 2}px 0`,
			textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
			fontWeight: 500,
			[theme.breakpoints.down('sm')]: {
				padding: `${theme.spacing.unit}px 0`,
			},
		},
		infoContainer: {
			display: 'flex',
			flexDirection: 'row',
			flexWrap: 'wrap',
			paddingLeft: `${theme.spacing.unit * 2}px`,
			[theme.breakpoints.only('xs')]: {
				paddingLeft: `${theme.spacing.unit}px`,
			},
		},
		addressContainer: {
			flexBasis: '48%',
			[theme.breakpoints.down('sm')]: {
				flexBasis: '98%',
			},
		},
		divider: {
			margin: '15px 0',
		},
		formActions: {
			display: 'flex',
			justifyContent: 'flex-end',
		},
		icon: {
			paddingRight: '5px',
		},
		button: {},
	})

export default withStyles(styles)(ParticipantInfo)
